import React from 'react';
import { InputBaseComponentProps } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { BaseActivityMessage } from '../BaseActivityMessage';
import { TextField } from '../TextField';

export interface NumericActivityMessageProps {
  /**
   * Current numeric value
   */
  value: string;
  /**
   * Callback when form is submitted
   */
  onSubmit: (value: string) => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Label/aria-label for the numeric input
   */
  label?: string;
  /**
   * Maximum length for numeric input
   */
  maxLength?: number;
  /**
   * Minimum length for numeric input
   */
  minLength?: number;
  /**
   * Message content to display above the numeric input
   */
  children?: React.ReactNode;
  /**
   * Sender type: 'bot' or 'user'
   */
  sender: 'bot' | 'user';
  /**
   * Avatar URL for the sender
   */
  avatarUrl?: string;
  /**
   * Timestamp for the message
   */
  timestamp?: string;
}

/**
 * NumericActivityMessage displays a numeric input interface with submit and optional skip buttons.
 * This component is designed for collecting numeric values in chat-like interfaces,
 * with support for length validation and custom labels.
 */
export const NumericActivityMessage: React.FC<NumericActivityMessageProps> = ({
  value,
  onSubmit,
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Send',
  onSkip,
  disabled = false,
  label,
  maxLength,
  minLength = 1,
  children,
  sender,
  avatarUrl,
  timestamp,
}) => {
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      numericPrompt: value || '',
    },
  });

  const onlyNumeric = (e: InputBaseComponentProps) => {
    if (!/[0-9]|Enter/.test(e.key)) {
      e.preventDefault();
    }
  };

  const numericValue = watch('numericPrompt');

  const handleFormSubmit = ({ numericPrompt }: { numericPrompt: string }) => {
    onSubmit(numericPrompt);
  };

  const isSubmitDisabled = numericValue.length < minLength || numericValue.length > (maxLength ?? Infinity) || disabled;

  return (
    <BaseActivityMessage
      allowSkip={allowSkip}
      skipText={skipText}
      submitText={submitText}
      onSkip={onSkip}
      disabled={disabled}
      submitEnabled={!isSubmitDisabled}
      sender={sender}
      avatarUrl={avatarUrl}
      timestamp={timestamp}
      onSubmit={() => {}}
      useForm={true}
      onFormSubmit={handleSubmit(handleFormSubmit)}
      renderInput={() => (
        <Controller
          name="numericPrompt"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                onKeyPress: onlyNumeric,
                maxLength: maxLength || undefined,
                minLength: minLength,
              }}
              slotProps={{
                htmlInput: {
                  'aria-label': label || 'Enter numbers',
                },
              }}
              disabled={disabled}
            />
          )}
        />
      )}
    >
      {children}
    </BaseActivityMessage>
  );
};
